
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Family } from "@/types";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

interface FamilySettingsFormProps {
  family: Family;
  onUpdate: (name: string, description: string, icon: string) => Promise<void>;
  isLoading: boolean;
}

export function FamilySettingsForm({
  family,
  onUpdate,
  isLoading,
}: FamilySettingsFormProps) {
  const [name, setName] = useState(family.name);
  const [description, setDescription] = useState(family.description);
  const [icon, setIcon] = useState(family.icon);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) {
      toast({
        title: "Missing Fields",
        description: "Family name is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      await onUpdate(name, description, icon);
      toast({
        title: "Success!",
        description: "Family settings updated successfully.",
      });
    } catch (error) {
      console.error("Error updating family settings:", error);
      toast({
        title: "Error",
        description: "Failed to update family settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Family Settings</CardTitle>
        <CardDescription>
          Update your family profile information
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Family Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="icon">Family Icon (emoji)</Label>
            <Input
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Enter an emoji as your family icon"
              maxLength={2}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
